import React, {SyntheticEvent, useEffect, useState} from "react";
import { useHistory, useParams} from "react-router-dom";
import {connect, ConnectedProps, useDispatch} from "react-redux";
import {setAlert} from "../../../actions/alert";
import {createUser, getAllOrganizations, getUserById} from "../../../actions/users";
import {
    GenericFormContainer,
    Spinner,
    FormChips,
    Leap4Breadcrumbs,
    AlertTypes,
    DialogChangePassword,
    Mode,
    Grid,
    Mui,
    MuiIcons,
    deleteInstance
} from "@leap4/frontend";
import {IOrganization, IUser, User} from "@leap4/auth";
import * as _ from 'lodash';
import {RootState} from "../../../interfaces/root-state.interface";
import {UsersGState} from "../../../interfaces/user-global-state.interface";
import {urlService} from "../../routing/urlsService";

const {TextField} = Mui;
const {Toc: TocIcon, Create: CreateIcon, AutoFixNormal: AutoFixNormalIcon, GpsFixed: GpsFixedIcon} = MuiIcons;

const mapStateToProps = (state: RootState) => ({
    users: state.users
});

const mapDispatch = {
    setAlert
};

const validate =
    /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;


const connector = connect(mapStateToProps, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;


interface AddEditUserProps extends PropsFromRedux {
    users: UsersGState;
    setAlert: any;
    addUser: any;
    updateUser: any;
    bIsLoading: boolean;
}


export interface IFormData extends Partial<IUser> {
    password2?: string;
}


const initialUserState: any = {
    email: "",
    password: "",
    organization: "",
    organizations: [],
    name: "",
    password2: "",
    metadata: {},
    metadataMulti: {},
    isActive: true
}


const _AddUser = ({setAlert}: AddEditUserProps): JSX.Element => {
    const history = useHistory();
    const params = useParams<{ id?: string }>();
    const dispatch = useDispatch();
    const [isAddPage, setIsAddPage] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);


    const [user, setUser] = useState<IFormData>({...initialUserState});
    const [optionalOrganizations, setOptionalOrganizations] = useState<Array<IOrganization>>([]);

    const handleFormChange = (e: any) => {
        let {name, value} = e.target;
        setUser({...user, [name]: value});
    }

    const updateUserOrganizations = (organizations: string[]) => {
        setUser({...user, organizations: organizations})
    }


    // Form submit.
    const handleSubmit = async (ev: SyntheticEvent) => {
        // For block rendering.
        ev?.preventDefault();
        if (!user) {
            setAlert("Error fill in the props", AlertTypes.FAILURE);
            return;
        }
        if (user.password !== user.password2 || user.password === '') {
            setAlert("Passwords aren't matches.", AlertTypes.FAILURE);
            return;
        }
        if (!user.email.match(validate)) {
            setAlert("Email in invalid", AlertTypes.FAILURE);
            return;
        }
        if (user.name === '') {
            setAlert("Please write the name!", AlertTypes.FAILURE);
            return;
        }
        try {
            setIsLoading(true)
            await createUser(dispatch, user);
        } catch(err) {
            setAlert(err.toString(), AlertTypes.FAILURE)
        } finally {
            setIsLoading(false)
        }
        setAlert("User " + user.name + " created successfully", AlertTypes.SUCCESS)
        history.push(urlService.users());
    };


    // Init form.
    useEffect(() => {
        (async () => {
            setIsLoading(true);

            // Fetching all organizations.
            const organizationsData: IOrganization[] = await getAllOrganizations(dispatch);

            // Convert tje organizations objects to ISelectable interface.
            setOptionalOrganizations(organizationsData);
            const id = params.id;
            // For editUser page.
            if (id && id !== "add") {
                setIsAddPage(false);
                const userData: User = await getUserById(dispatch, id.toString());
                _.defaultsDeep(userData, initialUserState);
                setUser({...userData});
            }

            setIsLoading(false);
        })()
    }, []);

    if (isLoading) return (
        <Spinner/>
    )
    return (
        <Grid mt={8}>
            <Leap4Breadcrumbs breadcrumbs={[
                {
                    text: "Users Dashboard",
                    icon: <TocIcon/>,
                    link: "/users"
                },
                {
                    text: isAddPage ? "Create" : "Update",
                    link: isAddPage ? "/users/add" : "/users/edit",
                    icon: isAddPage ? <CreateIcon/> : <AutoFixNormalIcon/>
                },
                {
                    text: user.name || "New User",
                    link: "",
                    icon: <GpsFixedIcon/>
                }
            ]}
            />
            {isLoading ? (
                <Spinner/>
            ) : (
                <GenericFormContainer mode={isAddPage ? Mode.Create : Mode.Edit}
                                      disabled={isLoading}
                                      onCreateOrEditFunc={(ev: any) => handleSubmit(ev)}
                                      onDeleteFunction={() => deleteInstance(params.id as any, "user", history, dispatch)}>
                    <Grid container item alignItems="center" spacing={2} sm={12}>
                        <Grid item xs={5}>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={user?.email}
                                autoFocus
                                onChange={handleFormChange}

                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                label="Full name"
                                name="name"
                                autoComplete="name"
                                autoFocus
                                onChange={handleFormChange}
                                value={user?.name}
                            />
                        </Grid>
                        {!isAddPage && params.id &&
                            <Grid item justifyContent="center" xs={3}>
                                <DialogChangePassword id={params.id} schemaName={"user"}/>
                            </Grid>
                        }
                        {
                            // We want to display password fields only in Add user mode.
                            isAddPage &&
                            <Grid container item alignItems="center" spacing={2} sm={12}>
                                <Grid item xs={5}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        name="password"
                                        label="Password"
                                        type="password"
                                        value={user?.password}
                                        onChange={handleFormChange}
                                        autoComplete="current-password"
                                    />
                                </Grid>
                                <Grid item xs={5}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        name="password2"
                                        label="Repeat Password"
                                        type="password"
                                        value={user?.password2}
                                        onChange={handleFormChange}
                                        autoComplete="current-password"
                                    />
                                </Grid>
                            </Grid>
                        }
                        <Grid container item alignItems="center" spacing={2} sm={12}>
                            <Grid item>
                                <FormChips optionsList={optionalOrganizations} valueKey={"_id"} labelKey={"name"}
                                           onUpdateItems={updateUserOrganizations}
                                           inputName={"User Organizations"} enableCreate={false}
                                           chips={(user.organizations || []).map(x => x.toString())}
                                           link={(id: string) => `/organizations/${id}`} pureCreate={false}/>
                            </Grid>
                        </Grid>

                    </Grid>
                </GenericFormContainer>
            )}
        </Grid>
    );
}

export const AddUser = connect(mapStateToProps, {
    setAlert
})(_AddUser);